import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Zap, 
  ArrowRight,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Users,
  FileText,
  AlertTriangle,
  Building2,
  MapPin,
  Briefcase,
  Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SharedReportPage = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAudit = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/audit/${auditId}`);
      setAudit(response.data);
    } catch (error) {
      console.error("Failed to fetch audit:", error);
      toast.error("Report not found or has expired");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [auditId, navigate]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  const getScoreColor = (score) => {
    if (score >= 60) return "text-[#d97706]";  // Orange for 60+
    return "text-[#dc2626]";  // Red for below 60
  };

  const getScoreStatus = (score) => {
    if (score >= 80) return { label: "Minor Gaps - Quick Wins Available", color: "bg-[#f59e0b]/10 text-[#d97706] border-[#f59e0b]/20" };
    if (score >= 60) return { label: "Improvement Needed", color: "bg-[#f59e0b]/10 text-[#d97706] border-[#f59e0b]/20" };
    if (score >= 40) return { label: "Significant Gaps", color: "bg-[#dc2626]/10 text-[#dc2626] border-[#dc2626]/20" };
    if (score >= 20) return { label: "Critical Issues", color: "bg-[#dc2626]/10 text-[#dc2626] border-[#dc2626]/20" };
    return { label: "Urgent Action Required", color: "bg-[#dc2626]/10 text-[#dc2626] border-[#dc2626]/20" };
  };

  const renderCompetitor = (competitor, index) => {
    const strengths = competitor.strengths || [];
    return (
      <div key={index} className="p-4 bg-slate-50 rounded-lg border border-slate-100">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#0a2540] flex items-center justify-center text-white text-sm font-semibold">
              {competitor.rank}
            </div>
            <span className="font-semibold text-[#0a2540]">{competitor.name}</span>
          </div>
          <Badge className="bg-[#3ab046]/10 text-[#3ab046] border border-[#3ab046]/20">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Visible
          </Badge>
        </div>
        <p className="text-sm text-slate-600 mb-2">{competitor.why_visible}</p>
        <div className="flex flex-wrap gap-2">
          {strengths[0] && <Badge variant="outline" className="text-xs">{strengths[0]}</Badge>}
        </div>
      </div>
    );
  };

  const renderGap = (gap, index) => {
    const borderColor = gap.priority === 'High' ? '#d41414' : gap.priority === 'Medium' ? '#d4af37' : '#3ab046';
    const priorityClass = gap.priority === 'High' ? 'bg-red-100 text-red-700' : gap.priority === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700';
    
    return (
      <div key={index} className="p-4 border-l-4 bg-white rounded-r-lg shadow-sm" style={{ borderColor }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Badge className={priorityClass}>{gap.priority} Priority</Badge>
            <Badge variant="outline">{gap.category}</Badge>
          </div>
        </div>
        <p className="font-medium text-[#0a2540] mb-1">{gap.issue}</p>
        <p className="text-sm text-slate-600">
          <span className="font-medium text-[#3ab046]">Recommendation:</span> {gap.recommendation}
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-[#0a2540]/20 border-t-[#d4af37] animate-spin" />
          <p className="text-slate-600">Loading shared report...</p>
        </div>
      </div>
    );
  }

  if (!audit) return null;

  const scoreStatus = getScoreStatus(audit.visibility_score);
  const competitors = audit.competitors || [];
  const gaps = audit.gaps || [];

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#0a2540] flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#d4af37]" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#0a2540]">PromptReady</span>
            </div>
            <Button 
              onClick={() => navigate("/audit")} 
              className="bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] rounded-full font-semibold"
              data-testid="get-your-own-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Get Your Own Report
            </Button>
          </div>
        </div>
      </header>

      {/* CTA Banner */}
      <div className="bg-gradient-to-r from-[#0a2540] to-[#162536] text-white py-4">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-[#d4af37]" />
            <p className="text-sm md:text-base">
              <span className="font-semibold">Is AI recommending YOUR competitors?</span>
              <span className="text-slate-300 ml-2">Find out in 60 seconds - completely free</span>
            </p>
          </div>
          <Button 
            onClick={() => navigate("/audit")} 
            className="bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] rounded-full font-semibold whitespace-nowrap"
            data-testid="cta-banner-btn"
          >
            Run Your Free AI Audit
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        {/* Shared Report Notice */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FileText className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-blue-800">Shared AI Visibility Report</p>
            <p className="text-xs text-blue-600">This report was shared with you by {audit.business_name}</p>
          </div>
        </div>

        {/* Report Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Badge className={`${scoreStatus.color} border px-3 py-1`}>
              {audit.is_visible ? <CheckCircle2 className="w-4 h-4 mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
              {scoreStatus.label}
            </Badge>
            <span className="text-sm text-slate-500">
              Report generated {new Date(audit.timestamp).toLocaleDateString()}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#0a2540]">AI Visibility Audit Report</h1>
          <p className="mt-2 text-lg text-slate-600">
            Analysis for <span className="font-semibold">{audit.business_name}</span>
          </p>
        </div>

        {/* Business Info Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0a2540]/5 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#0a2540]" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Business</p>
                <p className="font-medium text-[#0a2540]">{audit.business_name}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0a2540]/5 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-[#0a2540]" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Service</p>
                <p className="font-medium text-[#0a2540] capitalize">{audit.service_type.replace('-', ' ')}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0a2540]/5 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#0a2540]" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Location</p>
                <p className="font-medium text-[#0a2540]">{audit.location}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="bg-white rounded-xl border border-slate-100 shadow-sm">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="text-lg font-semibold text-[#0a2540] flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#d4af37]" />
                  Visibility Score
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="relative w-40 h-40 mx-auto">
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="45" fill="none" stroke="#e2e8f0" strokeWidth="8" />
                      <circle cx="50" cy="50" r="45" fill="none" stroke={audit.visibility_score >= 60 ? "#f59e0b" : "#dc2626"} strokeWidth="8" strokeLinecap="round" strokeDasharray={`${audit.visibility_score * 2.83} 283`} transform="rotate(-90 50 50)" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className={`text-4xl font-bold ${getScoreColor(audit.visibility_score)}`}>{audit.visibility_score}%</span>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-slate-600">
                    {audit.visibility_score < 20 && "URGENT: Invisible to AI - customers sent to competitors"}
                    {audit.visibility_score >= 20 && audit.visibility_score < 40 && "CRITICAL: Rarely recommended - major opportunities lost"}
                    {audit.visibility_score >= 40 && audit.visibility_score < 60 && "WARNING: Significant visibility gaps"}
                    {audit.visibility_score >= 60 && audit.visibility_score < 80 && "Gaps in AI visibility - improvements needed"}
                    {audit.visibility_score >= 80 && "Minor gaps - quick wins available"}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0a2540] text-white rounded-xl border-0 shadow-lg">
              <CardContent className="p-6">
                <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Query Analyzed</p>
                <p className="font-mono text-lg text-[#d4af37]">"{audit.query_used}"</p>
              </CardContent>
            </Card>

            {/* CTA Card */}
            <Card className="bg-gradient-to-br from-[#d4af37] to-[#b5952f] text-[#0a2540] rounded-xl border-0 shadow-lg">
              <CardContent className="p-6 text-center">
                <Sparkles className="w-10 h-10 mx-auto mb-3" />
                <h3 className="font-heading text-xl font-bold mb-2">Want to check YOUR business?</h3>
                <p className="text-sm mb-4 opacity-90">Get your own AI Visibility Audit in 60 seconds</p>
                <Button 
                  onClick={() => navigate("/audit")} 
                  className="w-full bg-[#0a2540] text-white hover:bg-[#162536] rounded-full font-semibold"
                  data-testid="sidebar-cta-btn"
                >
                  Run Free Audit
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-white rounded-xl border border-slate-100 shadow-sm">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="text-lg font-semibold text-[#0a2540] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#d4af37]" />
                  Executive Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-slate-600 leading-relaxed">{audit.summary}</p>
              </CardContent>
            </Card>

            <Card className="bg-white rounded-xl border border-slate-100 shadow-sm">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="text-lg font-semibold text-[#0a2540] flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#d4af37]" />
                  Competitors Being Surfaced
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">These businesses appear when AI is asked about this service</p>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {competitors.map((competitor, index) => renderCompetitor(competitor, index))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white rounded-xl border border-slate-100 shadow-sm">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="text-lg font-semibold text-[#0a2540] flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-[#d4af37]" />
                  Gap Analysis & Recommendations
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">What needs to improve to increase AI visibility</p>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {gaps.map((gap, index) => renderGap(gap, index))}
                </div>
              </CardContent>
            </Card>

            {/* Bottom CTA */}
            <Card className="bg-gradient-to-r from-[#0a2540] to-[#162536] text-white rounded-xl border-0 shadow-xl">
              <CardContent className="p-8 text-center">
                <Sparkles className="w-12 h-12 mx-auto mb-4 text-[#d4af37]" />
                <h3 className="font-heading text-2xl font-semibold mb-2">Is AI Recommending Your Competitors?</h3>
                <p className="text-slate-300 mb-6 max-w-md mx-auto">
                  Find out exactly where your business stands in AI recommendations. Takes just 60 seconds.
                </p>
                <Button 
                  onClick={() => navigate("/audit")} 
                  className="bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] text-lg font-semibold rounded-full px-8 py-6"
                  data-testid="bottom-cta-btn"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Get Your Free AI Audit
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Sticky Bottom CTA (Mobile) */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-200 shadow-lg md:hidden">
        <Button 
          onClick={() => navigate("/audit")} 
          className="w-full bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] rounded-full font-semibold py-6"
          data-testid="mobile-cta-btn"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          Get Your Own Free Report
        </Button>
      </div>
    </div>
  );
};

export default SharedReportPage;
